<template>
  <Modal v-model="show" size="6xl" :prevent-outside-close="true">
    <template #title>
      <div class="flex items-center gap-2">
        <i class="fas fa-file-invoice text-primary-600 dark:text-primary-400"></i>
        <span>{{ editMode ? 'Edit' : 'Create' }} Quotation</span>
      </div>
    </template>

    <template #content>
      <div class="space-y-5">
        <!-- Client Info and Quotation Info in one row -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <!-- Client Info -->
          <div class="bg-white dark:bg-dark-800 p-4 rounded-lg border border-gray-200 dark:border-dark-700">
            <h3 class="text-base font-semibold text-surface-900 dark:text-surface-100 mb-2">
              <div v-if="editMode">
                <div v-if="localClient.first_name || localClient.last_name">{{ localClient.first_name || '' }} {{
                  localClient.last_name || '' }}</div>
                <p v-if="localClient.company_name">{{ localClient.company_name }}</p>
              </div>
              <div v-else>
                <div v-if="client.first_name || client.last_name">{{ client.first_name || '' }} {{ client.last_name ||
                  '' }}</div>
                <p v-if="client.company_name">{{ client.company_name }}</p>
              </div>
            </h3>
            <p v-if="editMode && localClient.address" class="text-sm text-surface-600 dark:text-surface-400 mb-1">
              {{ localClient.address }}
            </p>
            <p v-else-if="!editMode && client.address" class="text-sm text-surface-600 dark:text-surface-400 mb-1">
              {{ client.address }}
            </p>
            <p v-if="editMode && localClient.phone" class="text-sm text-surface-600 dark:text-surface-400">
              {{ localClient.phone }}
            </p>
            <p v-else-if="!editMode && client.phone" class="text-sm text-surface-600 dark:text-surface-400">
              {{ client.phone }}
            </p>
            <p v-if="editMode && localClient.email" class="text-sm text-surface-600 dark:text-surface-400">
              {{ localClient.email }}
            </p>
            <p v-else-if="!editMode && client.email" class="text-sm text-surface-600 dark:text-surface-400">
              {{ client.email }}
            </p>
          </div>
          <!-- Quotation Info -->
          <div class="bg-white dark:bg-dark-800 p-4 rounded-lg border border-gray-200 dark:border-dark-700 text-right">
            <p class="text-sm text-surface-600 dark:text-surface-400 mb-1">Quote Date: {{ formatDate(quoteDate) }}</p>
            <p class="text-sm text-surface-600 dark:text-surface-400 mb-1">Valid Until: {{ formatDate(validUntil) }}</p>
            <p class="text-sm text-surface-600 dark:text-surface-400">Quotation #: {{ quotationNumber }}</p>
          </div>
        </div>

        <!-- Products Table -->
        <div class="bg-surface-50 dark:bg-surface-800 rounded-lg border border-surface-300 dark:border-surface-600 overflow-hidden">
          <div class="overflow-x-auto">
            <table class="min-w-full divide-y divide-surface-300 dark:divide-surface-600">
              <thead class="bg-surface-100 dark:bg-surface-700">
                <tr>
                  <th
                    class="px-4 py-3 text-left text-xs font-medium text-surface-600 dark:text-surface-400 uppercase tracking-wider">
                    Description</th>
                  <th
                    class="px-4 py-3 text-center text-xs font-medium text-surface-600 dark:text-surface-400 uppercase tracking-wider">
                    Qty</th>
                  <th
                    class="px-4 py-3 text-right text-xs font-medium text-surface-600 dark:text-surface-400 uppercase tracking-wider">
                    Unit Price</th>
                  <th
                    class="px-4 py-3 text-center text-xs font-medium text-surface-600 dark:text-surface-400 uppercase tracking-wider">
                    Unit</th>
                  <th
                    class="px-4 py-3 text-center text-xs font-medium text-surface-600 dark:text-surface-400 uppercase tracking-wider">
                    Dimensions</th>
                  <th
                    class="px-4 py-3 text-center text-xs font-medium text-surface-600 dark:text-surface-400 uppercase tracking-wider">
                    Discount (Per Unit)</th>
                  <th
                    class="px-4 py-3 text-right text-xs font-medium text-surface-600 dark:text-surface-400 uppercase tracking-wider">
                    Total Discount</th>
                  <th
                    class="px-4 py-3 text-right text-xs font-medium text-surface-600 dark:text-surface-400 uppercase tracking-wider">
                    Pre-Discount</th>
                  <th
                    class="px-4 py-3 text-right text-xs font-medium text-surface-600 dark:text-surface-400 uppercase tracking-wider">
                    Total Price</th>
                  <th
                    class="px-4 py-3 text-center text-xs font-medium text-surface-600 dark:text-surface-400 uppercase tracking-wider">
                    Actions</th>
                </tr>
              </thead>
              <tbody class="bg-surface-50 dark:bg-surface-800 divide-y divide-surface-300 dark:divide-surface-600">
                <tr v-for="(item, index) in items" :key="index" class="hover:bg-surface-100 dark:hover:bg-surface-700">
                  <td class="px-4 py-3">
                    <textarea v-model="item.description" rows="2"
                      class="w-full text-sm border-surface-300 dark:border-surface-600 rounded-md shadow-sm focus:border-primary-500 focus:ring-primary-500 bg-surface-50 dark:bg-surface-700 text-surface-900 dark:text-surface-100 resize-none placeholder-surface-500 dark:placeholder-surface-400"
                      placeholder="Item description..."></textarea>
                    <div v-if="item.product_id" class="text-xs text-surface-600 dark:text-surface-400 mt-1">Product ID: {{
                      item.product_id }}</div>
                  </td>
                  <td class="px-4 py-3 text-center">
                    <input type="number" v-model.number="item.quantity" @input="updateSubtotal(index)" min="1"
                      class="w-16 text-center border-surface-300 dark:border-surface-600 rounded-md shadow-sm focus:border-primary-500 focus:ring-primary-500 bg-surface-50 dark:bg-surface-700 text-surface-900 dark:text-surface-100" />
                  </td>
                  <td class="px-4 py-3 text-right">
                    <input type="number" v-model.number="item.unit_price" @input="updateSubtotal(index)" min="0"
                      step="0.01"
                      class="w-24 text-right border-surface-300 dark:border-surface-600 rounded-md shadow-sm focus:border-primary-500 focus:ring-primary-500 bg-surface-50 dark:bg-surface-700 text-surface-900 dark:text-surface-100" />
                  </td>
                  <td class="px-4 py-3 text-center">
                    <div class="text-sm text-surface-600 dark:text-surface-400">{{ item.unit_of_measure }}</div>
                  </td>
                  <td class="px-4 py-3 text-center">
                    <div v-if="item.is_dimensions" class="flex items-center justify-center space-x-1">
                      <input type="number" v-model.number="item.width" @input="updateDimensions(index)" min="0"
                        step="0.01"
                        class="w-16 text-center border-surface-300 dark:border-surface-600 rounded-md shadow-sm focus:border-primary-500 focus:ring-primary-500 bg-surface-50 dark:bg-surface-700 text-surface-900 dark:text-surface-100 placeholder-surface-500 dark:placeholder-surface-400"
                        placeholder="Width" />
                      <span class="text-surface-600 dark:text-surface-400">×</span>
                      <input type="number" v-model.number="item.height" @input="updateDimensions(index)" min="0"
                        step="0.01"
                        class="w-16 text-center border-surface-300 dark:border-surface-600 rounded-md shadow-sm focus:border-primary-500 focus:ring-primary-500 bg-surface-50 dark:bg-surface-700 text-surface-900 dark:text-surface-100 placeholder-surface-500 dark:placeholder-surface-400"
                        placeholder="Height" />
                    </div>
                    <div v-else class="text-sm text-surface-600 dark:text-surface-400">N/A</div>
                  </td>
                  <td class="px-4 py-3 text-center">
                    <div class="flex items-center justify-center space-x-1">
                      <input type="number" v-model.number="item.discount_value" @input="updateSubtotal(index)" min="0"
                        :max="item.discount_type === 'percentage' ? 100 : null"
                        class="w-16 text-center border-surface-300 dark:border-surface-600 rounded-md shadow-sm focus:border-primary-500 focus:ring-primary-500 bg-surface-50 dark:bg-surface-700 text-surface-900 dark:text-surface-100 disabled:opacity-50 disabled:cursor-not-allowed"
                        :disabled="item.discount_type === 'none'" />
                      <select v-model="item.discount_type" @change="updateSubtotal(index)"
                        class="w-16 border-surface-300 dark:border-surface-600 rounded-md shadow-sm focus:border-primary-500 focus:ring-primary-500 bg-surface-50 dark:bg-surface-700 text-surface-900 dark:text-surface-100">
                        <option value="none">None</option>
                        <option value="percentage">%</option>
                        <option value="fixed">₱</option>
                      </select>
                    </div>
                  </td>
                  <td class="px-4 py-3 text-right">
                    <div class="text-sm text-green-600 dark:text-green-400 font-medium">
                      {{ formatCurrency(calculateItemTotalDiscount(item)) }}
                    </div>
                  </td>
                  <td class="px-4 py-3 text-right">
                    <div class="text-sm text-surface-600 dark:text-surface-400">{{ formatCurrency(item.pre_discount_subtotal)
                    }}</div>
                  </td>
                  <td class="px-4 py-3 text-right">
                    <div class="text-sm font-medium"
                      :class="item.discount_type && item.discount_type !== 'none' && item.discount_value > 0 ? 'text-success-600 dark:text-success-400' : 'text-surface-900 dark:text-surface-100'">
                      {{ formatCurrency(item.subtotal) }}
                    </div>
                  </td>
                  <td class="px-4 py-3 text-center">
                    <button @click="removeItem(index)" class="text-red-600 hover:text-red-900" title="Remove item">
                      <i class="fas fa-trash-alt"></i>
                    </button>
                  </td>
                </tr>

                <!-- Empty state -->
                <tr v-if="items.length === 0">
                  <td colspan="10" class="px-4 py-4 text-center text-sm text-surface-600 dark:text-surface-400">
                    No items added yet. Add products or custom items below.
                  </td>
                </tr>

                <!-- Subtotal row -->
                <tr v-if="items.length > 0" class="bg-surface-100 dark:bg-surface-700">
                  <td colspan="8" class="px-4 py-3 text-right text-sm font-medium text-surface-900 dark:text-surface-100">
                    Subtotal:</td>
                  <td class="px-4 py-3 text-right text-sm font-medium text-surface-900 dark:text-surface-100">{{
                    formatCurrency(subtotalAmount) }}</td>
                  <td></td>
                </tr>

                <!-- Discount row -->
                <tr v-if="items.length > 0" class="bg-surface-100 dark:bg-surface-700">
                  <td colspan="7" class="px-4 py-3 text-right text-sm font-medium text-surface-900 dark:text-surface-100">
                    Discount:</td>
                  <td class="px-4 py-3 text-center">
                    <div class="flex items-center justify-center space-x-1">
                      <input type="number" v-model.number="totalDiscountValue" @input="updateTotalDiscount" min="0"
                        :max="totalDiscountType === 'percentage' ? 100 : null"
                        class="w-16 text-center border-surface-300 dark:border-surface-600 rounded-md shadow-sm focus:border-primary-500 focus:ring-primary-500 bg-surface-50 dark:bg-surface-700 text-surface-900 dark:text-surface-100 disabled:opacity-50 disabled:cursor-not-allowed"
                        :disabled="totalDiscountType === 'none'" />
                      <select v-model="totalDiscountType" @change="updateTotalDiscount"
                        class="w-16 border-surface-300 dark:border-surface-600 rounded-md shadow-sm focus:border-primary-500 focus:ring-primary-500 bg-surface-50 dark:bg-surface-700 text-surface-900 dark:text-surface-100">
                        <option value="none">None</option>
                        <option value="percentage">%</option>
                        <option value="fixed">₱</option>
                      </select>
                    </div>
                  </td>
                  <td class="px-4 py-3 text-right text-sm font-medium text-surface-900 dark:text-surface-100">{{
                    formatCurrency(totalDiscountAmount) }}</td>
                  <td></td>
                </tr>

                <!-- Total row -->
                <tr v-if="items.length > 0" class="bg-surface-100 dark:bg-surface-700 font-bold">
                  <td colspan="8" class="px-4 py-3 text-right text-sm font-bold text-surface-900 dark:text-surface-100">Total:
                  </td>
                  <td class="px-4 py-3 text-right text-sm font-bold text-surface-900 dark:text-surface-100">{{
                    formatCurrency(totalAmount) }}</td>
                  <td></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Add Items Section -->
        <div class="bg-white dark:bg-dark-800 p-5 rounded-lg border border-gray-200 dark:border-dark-700">
          <div class="flex flex-col sm:flex-row justify-between mb-4 gap-3">
            <h3 class="text-base font-semibold text-surface-900 dark:text-surface-100">Add Items</h3>
            <div class="flex flex-wrap gap-2">
              <button @click="showProductSelector = true" class="btn btn-primary text-sm">
                <i class="fas fa-plus mr-1"></i> Add Product
              </button>
              <button @click="addCustomItem" class="btn btn-secondary text-sm">
                <i class="fas fa-plus mr-1"></i> Add Custom Item
              </button>
            </div>
          </div>

          <!-- Custom Item Form (conditionally shown) -->
          <div v-if="showCustomItemForm"
            class="bg-surface-50 dark:bg-surface-700/50 p-4 rounded-md mb-4 border border-surface-200 dark:border-surface-600">
            <h4 class="text-sm font-medium text-surface-900 dark:text-surface-100 mb-3">Add Custom Item</h4>
            <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label class="block text-xs font-medium text-surface-700 dark:text-surface-300 mb-1">Description</label>
                <input type="text" v-model="customItem.description"
                  class="w-full border-surface-300 dark:border-surface-600 rounded-md shadow-sm focus:border-primary-500 focus:ring-primary-500 bg-surface-50 dark:bg-surface-700 text-surface-900 dark:text-surface-100 placeholder-surface-500 dark:placeholder-surface-400"
                  placeholder="Enter description" />
              </div>
              <div>
                <label class="block text-xs font-medium text-surface-700 dark:text-surface-300 mb-1">Quantity</label>
                <input type="number" v-model.number="customItem.quantity" min="1"
                  class="w-full border-surface-300 dark:border-surface-600 rounded-md shadow-sm focus:border-primary-500 focus:ring-primary-500 bg-surface-50 dark:bg-surface-700 text-surface-900 dark:text-surface-100 placeholder-surface-500 dark:placeholder-surface-400"
                  placeholder="Enter quantity" />
              </div>
              <div>
                <label class="block text-xs font-medium text-surface-700 dark:text-surface-300 mb-1">Unit Price</label>
                <input type="number" v-model.number="customItem.unit_price" min="0" step="0.01"
                  class="w-full border-surface-300 dark:border-surface-600 rounded-md shadow-sm focus:border-primary-500 focus:ring-primary-500 bg-surface-50 dark:bg-surface-700 text-surface-900 dark:text-surface-100 placeholder-surface-500 dark:placeholder-surface-400"
                  placeholder="Enter unit price" />
              </div>
            </div>

            <div class="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div class="flex items-center">
                <input type="checkbox" id="hasDimensions" v-model="customItem.is_dimensions"
                  class="rounded border-surface-300 dark:border-surface-600 text-primary-600 focus:ring-primary-500 bg-surface-50 dark:bg-surface-700" />
                <label for="hasDimensions" class="ml-2 text-xs font-medium text-surface-700 dark:text-surface-300">Has
                  dimensions</label>
              </div>

              <div>
                <label class="block text-xs font-medium text-surface-700 dark:text-surface-300 mb-1">Unit of Measure</label>
                <select v-model="customItem.unit_of_measure"
                  class="w-full border-surface-300 dark:border-surface-600 rounded-md shadow-sm focus:border-primary-500 focus:ring-primary-500 bg-surface-50 dark:bg-surface-700 text-surface-900 dark:text-surface-100">
                  <option value="pc">pc</option>
                  <option value="m">m</option>
                  <option value="ft">ft</option>
                  <option value="in">in</option>
                  <option value="cm">cm</option>
                  <option value="set">set</option>
                </select>
              </div>

              <div>
                <label class="block text-xs font-medium text-surface-700 dark:text-surface-300 mb-1">Discount (Per
                  Unit)</label>
                <div class="flex space-x-2">
                  <input type="number" v-model.number="customItem.discount_value" min="0"
                    :max="customItem.discount_type === 'percentage' ? 100 : null"
                    class="w-full border-surface-300 dark:border-surface-600 rounded-md shadow-sm focus:border-primary-500 focus:ring-primary-500 bg-surface-50 dark:bg-surface-700 text-surface-900 dark:text-surface-100 placeholder-surface-500 dark:placeholder-surface-400 disabled:opacity-50 disabled:cursor-not-allowed"
                    placeholder="Per unit discount" :disabled="customItem.discount_type === 'none'" />
                  <select v-model="customItem.discount_type"
                    class="w-20 border-surface-300 dark:border-surface-600 rounded-md shadow-sm focus:border-primary-500 focus:ring-primary-500 bg-surface-50 dark:bg-surface-700 text-surface-900 dark:text-surface-100">
                    <option value="none">None</option>
                    <option value="percentage">%</option>
                    <option value="fixed">₱</option>
                  </select>
                </div>
              </div>
            </div>

            <div class="mt-4 flex justify-end space-x-2">
              <button @click="cancelCustomItem"
                class="px-3 py-1.5 bg-surface-200 dark:bg-surface-700 text-surface-700 dark:text-surface-300 text-xs rounded-md hover:bg-surface-300 dark:hover:bg-surface-600 focus:outline-none focus:ring-2 focus:ring-surface-500">
                Cancel
              </button>
              <button @click="saveCustomItem"
                class="px-3 py-1.5 bg-blue-600 text-white text-xs rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                :disabled="!isCustomItemValid">
                Add Item
              </button>
            </div>

            <!-- Dimensions inputs (shown only when has_dimensions is checked) -->
            <div v-if="customItem.is_dimensions" class="mt-4 p-3 bg-surface-100 dark:bg-surface-700 rounded-md">
              <label class="block text-xs font-medium text-surface-700 dark:text-surface-300 mb-2">Dimensions</label>
              <div class="flex items-center space-x-2">
                <input type="number" v-model.number="customItem.width" min="0" step="0.01"
                  class="w-24 text-center border-surface-300 dark:border-surface-600 rounded-md shadow-sm focus:border-primary-500 focus:ring-primary-500 bg-surface-50 dark:bg-surface-700 text-surface-900 dark:text-surface-100 placeholder-surface-500 dark:placeholder-surface-400"
                  placeholder="Width" />
                <span class="text-surface-600 dark:text-surface-400">×</span>
                <input type="number" v-model.number="customItem.height" min="0" step="0.01"
                  class="w-24 text-center border-surface-300 dark:border-surface-600 rounded-md shadow-sm focus:border-primary-500 focus:ring-primary-500 bg-surface-50 dark:bg-surface-700 text-surface-900 dark:text-surface-100 placeholder-surface-500 dark:placeholder-surface-400"
                  placeholder="Height" />
              </div>
            </div>
          </div>
        </div>

        <!-- Notes Section -->
        <div class="bg-white dark:bg-dark-800 p-5 rounded-lg border border-gray-200 dark:border-dark-700">
          <h3 class="text-base font-semibold text-surface-900 dark:text-surface-100 mb-3">Notes</h3>
          <textarea v-model="notes" rows="3" class="input-field w-full"
            placeholder="Enter any additional notes or special instructions for this quotation..."></textarea>
        </div>

        <!-- Email Notification Option -->
        <div class="bg-white dark:bg-dark-800 p-5 rounded-lg border border-gray-200 dark:border-dark-700">
          <div class="flex items-center">
            <input type="checkbox" id="sendEmailOnApproval" v-model="sendEmailOnApproval"
              class="h-4 w-4 text-primary-600 focus:ring-primary-500 border-surface-300 dark:border-surface-600 rounded bg-surface-50 dark:bg-surface-700">
            <label for="sendEmailOnApproval" class="ml-3 block text-sm text-surface-900 dark:text-surface-100">
              <span class="font-medium">Send email notification to client when quotation is approved</span>
              <p class="text-xs text-surface-600 dark:text-surface-400 mt-1">
                If checked, an email will be automatically sent to the client when this quotation is approved.
              </p>
            </label>
          </div>
        </div>
      </div>
    </template>

    <template #footer>
      <div class="flex flex-col sm:flex-row justify-between gap-3">
        <button @click="show = false" class="btn btn-ghost">
          Cancel
        </button>
        <div class="flex space-x-2">
          <button @click="generateQuotation"
            class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            :disabled="items.length === 0 || loading">
            {{ editMode ? 'Update Quotation' : 'Create Quotation' }}
          </button>
        </div>
      </div>
    </template>
  </Modal>

  <!-- Product Selector Modal -->
  <Modal v-model="showProductSelector" size="4xl" :prevent-outside-close="true">
    <template #title>
      <div class="flex items-center gap-2">
        <i class="fas fa-box text-primary-600 dark:text-primary-400"></i>
        <span>Select Product</span>
      </div>
    </template>

    <template #content>
      <div class="space-y-4">
        <!-- Search and Filter -->
        <div class="flex flex-col sm:flex-row gap-3">
          <div class="flex-1">
            <label class="block text-xs font-medium text-surface-700 dark:text-surface-300 mb-1">Search Products</label>
            <input type="text" v-model="productSearch" placeholder="Search products..."
              class="w-full border-surface-300 dark:border-surface-600 rounded-md shadow-sm focus:border-primary-500 focus:ring-primary-500 bg-surface-50 dark:bg-surface-700 text-surface-900 dark:text-surface-100 placeholder-surface-500 dark:placeholder-surface-400" />
          </div>
          <div class="sm:w-64">
            <label class="block text-xs font-medium text-surface-700 dark:text-surface-300 mb-1">Category</label>
            <select v-model="productCategoryFilter"
              class="w-full border-surface-300 dark:border-surface-600 rounded-md shadow-sm focus:border-primary-500 focus:ring-primary-500 bg-surface-50 dark:bg-surface-700 text-surface-900 dark:text-surface-100">
              <option value="">All Categories</option>
              <option v-for="category in productCategories" :key="category.category_id" :value="category.category_id">
                {{ category.name }}
              </option>
            </select>
          </div>
        </div>

        <!-- Products Table -->
        <div class="bg-surface-50 dark:bg-surface-800 rounded-lg border border-surface-300 dark:border-surface-600 overflow-hidden">
          <div class="overflow-x-auto max-h-96">
            <table class="min-w-full divide-y divide-surface-300 dark:divide-surface-600">
              <thead class="bg-surface-100 dark:bg-surface-700 sticky top-0">
                <tr>
                  <th
                    class="px-4 py-3 text-left text-xs font-medium text-surface-600 dark:text-surface-400 uppercase tracking-wider">
                    Name</th>
                  <th
                    class="px-4 py-3 text-left text-xs font-medium text-surface-600 dark:text-surface-400 uppercase tracking-wider">
                    Category</th>
                  <th
                    class="px-4 py-3 text-right text-xs font-medium text-surface-600 dark:text-surface-400 uppercase tracking-wider">
                    Price</th>
                  <th
                    class="px-4 py-3 text-center text-xs font-medium text-surface-600 dark:text-surface-400 uppercase tracking-wider">
                    Unit</th>
                  <th
                    class="px-4 py-3 text-center text-xs font-medium text-surface-600 dark:text-surface-400 uppercase tracking-wider">
                    Dimensions</th>
                  <th
                    class="px-4 py-3 text-center text-xs font-medium text-surface-600 dark:text-surface-400 uppercase tracking-wider">
                    Actions</th>
                </tr>
              </thead>
              <tbody class="bg-surface-50 dark:bg-surface-800 divide-y divide-surface-300 dark:divide-surface-600">
                <template v-if="loadingProducts">
                  <tr>
                    <td colspan="6" class="px-4 py-4 text-center text-sm text-surface-600 dark:text-surface-400">
                      <i class="fas fa-spinner fa-spin mr-2"></i> Loading products...
                    </td>
                  </tr>
                </template>
                <template v-else-if="filteredProducts.length === 0">
                  <tr>
                    <td colspan="6" class="px-4 py-4 text-center text-sm text-surface-600 dark:text-surface-400">
                      No products found
                    </td>
                  </tr>
                </template>
                <template v-else>
                  <tr v-for="product in filteredProducts" :key="product.id"
                    class="hover:bg-surface-100 dark:hover:bg-surface-700">
                    <td class="px-4 py-3 text-sm text-surface-900 dark:text-surface-100">{{ product.name }}</td>
                    <td class="px-4 py-3 text-sm text-surface-600 dark:text-surface-400">{{ product.category_name }}</td>
                    <td class="px-4 py-3 text-sm text-surface-900 dark:text-surface-100 text-right">{{
                      formatCurrency(product.base_price) }}</td>
                    <td class="px-4 py-3 text-sm text-surface-600 dark:text-surface-400 text-center">{{
                      product.unit_of_measure }}</td>
                    <td class="px-4 py-3 text-sm text-surface-600 dark:text-surface-400 text-center">
                      {{ product.has_dimensions ? 'Yes' : 'No' }}
                    </td>
                    <td class="px-4 py-3 text-center">
                      <button @click="addProduct(product)"
                        class="px-3 py-1.5 bg-blue-600 text-white text-xs rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500">
                        Add
                      </button>
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </template>

    <template #footer>
      <div class="flex justify-end">
        <button @click="showProductSelector = false"
          class="px-4 py-2 bg-surface-200 dark:bg-surface-700 text-surface-700 dark:text-surface-300 rounded-md hover:bg-surface-300 dark:hover:bg-surface-600 focus:outline-none focus:ring-2 focus:ring-surface-500">
          Close
        </button>
      </div>
    </template>
  </Modal>

  <!-- Session Restore Confirmation Modal -->
  <Modal v-model="showRestoreConfirmation" size="md">
    <template #title>
      <div class="flex items-center gap-2 text-blue-600">
        <i class="fas fa-history"></i>
        <span>Restore Draft Quotation</span>
      </div>
    </template>

    <template #content>
      <div class="space-y-3">
        <p class="text-gray-700 dark:text-dark-300">
          We found a previous draft quotation for this client. Would you like to restore it and continue where you left
          off?
        </p>
        <div class="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
          <p class="text-sm text-blue-800 dark:text-blue-300">
            <i class="fas fa-info-circle mr-2"></i>
            This will restore all items, notes, and settings from your previous session.
          </p>
        </div>
      </div>
    </template>

    <template #footer>
      <div class="flex justify-end space-x-3">
        <button @click="discardSession"
          class="px-4 py-2 bg-gray-200 dark:bg-dark-700 text-gray-700 dark:text-dark-300 rounded-md hover:bg-gray-300 dark:hover:bg-dark-600 focus:outline-none focus:ring-2 focus:ring-gray-500">
          Start Fresh
        </button>
        <button @click="restoreSession" class="btn btn-primary">
          <i class="fas fa-undo mr-2"></i>
          Restore Draft
        </button>
      </div>
    </template>
  </Modal>
</template>

<script setup>
import { ref, computed, onMounted, watch, nextTick, onBeforeUnmount } from 'vue';
import { useToast } from '@/composables/useToast';
import { useMasterData } from '@/composables/useMasterData';
import Modal from '@/components/common/Modal.vue';
import { format, addDays, parseISO } from 'date-fns';
import { quotationsApi, productsApi, productCategoriesApi } from '@/services/api';

const toast = useToast();
const masterData = useMasterData();

// Props
const props = defineProps({
  client: {
    type: Object,
    required: false,
    default: () => ({})
  },
  modelValue: {
    type: Boolean,
    default: false
  },
  editMode: {
    type: Boolean,
    default: false
  },
  quotationId: {
    type: [String, Number],
    default: null
  }
});

// Emits
const emit = defineEmits(['update:modelValue', 'created']);

// Local client data for edit mode
const localClient = ref({});

// Reactive state
const show = computed({
  get: () => props.modelValue,
  set: (value) => {
    emit('update:modelValue', value);
    if (!value && props.editMode) {
      // When closing in edit mode, navigate back
      window.history.back();
    }
  }
});

const quoteDate = ref(new Date());
const validUntil = ref(addDays(new Date(), 30));
const quotationNumber = ref('');
const items = ref([]);
const notes = ref('');
const loading = ref(false);
const sendEmailOnApproval = ref(true); // Default to true

// Custom item form
const showCustomItemForm = ref(false);
const customItem = ref({
  description: '',
  quantity: 1,
  unit_price: 0,
  is_dimensions: false,
  width: null,
  height: null,
  dimension_unit: 'mm',
  unit_of_measure: 'pc',
  discount_value: 0,
  discount_type: 'none',
  is_custom_item: true
});

// Product selector
const showProductSelector = ref(false);
const products = ref([]);
const productCategories = ref([]);
const loadingProducts = ref(false);
const productSearch = ref('');
const productCategoryFilter = ref('');

// Change totalDiscountAmount from computed to ref
const totalDiscountAmount = ref(0);

// Auto-save functionality
const sessionKey = computed(() => {
  // Create unique session key based on client and edit mode
  const clientId = props.editMode ? props.quotationId : (props.client?.id || 'new');
  const mode = props.editMode ? 'edit' : 'create';
  return `quotation_session_${mode}_${clientId}`;
});

const saveSession = () => {
  if (props.editMode) {
    // Don't auto-save in edit mode to avoid conflicts
    return;
  }

  const sessionData = {
    items: items.value,
    notes: notes.value,
    quoteDate: quoteDate.value,
    validUntil: validUntil.value,
    quotationNumber: quotationNumber.value,
    totalDiscountType: totalDiscountType.value,
    totalDiscountValue: totalDiscountValue.value,
    totalDiscountAmount: totalDiscountAmount.value,
    sendEmailOnApproval: sendEmailOnApproval.value,
    client: props.client,
    timestamp: new Date().toISOString()
  };

  try {
    localStorage.setItem(sessionKey.value, JSON.stringify(sessionData));
    console.log('💾 Quotation session saved automatically');
  } catch (error) {
    console.warn('Failed to save quotation session:', error);
  }
};

const loadSession = () => {
  if (props.editMode) {
    // Don't load session in edit mode
    return false;
  }

  try {
    const savedData = localStorage.getItem(sessionKey.value);
    if (!savedData) return false;

    const sessionData = JSON.parse(savedData);

    // Check if session is recent (within 24 hours)
    const sessionTime = new Date(sessionData.timestamp);
    const now = new Date();
    const hoursDiff = (now - sessionTime) / (1000 * 60 * 60);

    if (hoursDiff > 24) {
      // Session too old, clear it
      clearSession();
      return false;
    }

    // Check if it's for the same client
    if (sessionData.client?.id !== props.client?.id) {
      return false;
    }

    // Restore data
    if (sessionData.items?.length > 0) {
      items.value = sessionData.items;
      notes.value = sessionData.notes || '';
      quoteDate.value = sessionData.quoteDate ? new Date(sessionData.quoteDate) : new Date();
      validUntil.value = sessionData.validUntil ? new Date(sessionData.validUntil) : addDays(new Date(), 30);
      quotationNumber.value = sessionData.quotationNumber || '';
      totalDiscountType.value = sessionData.totalDiscountType || 'none';
      totalDiscountValue.value = sessionData.totalDiscountValue || 0;
      totalDiscountAmount.value = sessionData.totalDiscountAmount || 0;
      sendEmailOnApproval.value = sessionData.sendEmailOnApproval !== undefined ? sessionData.sendEmailOnApproval : true;

      console.log('🔄 Quotation session restored');

      // Show notification to user
      toast.info('Draft quotation restored from previous session', {
        timeout: 4000
      });

      return true;
    }
  } catch (error) {
    console.warn('Failed to load quotation session:', error);
    clearSession();
  }

  return false;
};

const clearSession = () => {
  try {
    localStorage.removeItem(sessionKey.value);
    console.log('🗑️ Quotation session cleared');
  } catch (error) {
    console.warn('Failed to clear quotation session:', error);
  }
};

const showRestoreConfirmation = ref(false);
const hasSavedSession = ref(false);

// Computed properties
const totalAmount = computed(() => {
  const amount = subtotalAmount.value - totalDiscountAmount.value;
  return parseFloat(amount).toFixed(2);
});

const subtotalAmount = computed(() => {
  const total = items.value.reduce((total, item) => total + parseFloat(item.subtotal || 0), 0);
  return parseFloat(total).toFixed(2);
});

const totalDiscountValue = ref(0);
const totalDiscountType = ref('none');

const filteredProducts = computed(() => {
  let filtered = [...products.value];

  // Apply search filter
  if (productSearch.value) {
    const search = productSearch.value.toLowerCase();
    filtered = filtered.filter(product =>
      product.name.toLowerCase().includes(search) ||
      product.description?.toLowerCase().includes(search)
    );
  }

  // Apply category filter
  if (productCategoryFilter.value) {
    filtered = filtered.filter(product =>
      product.category_id === parseInt(productCategoryFilter.value)
    );
  }

  return filtered;
});

const isCustomItemValid = computed(() => {
  return (
    customItem.value.description.trim() !== '' &&
    customItem.value.quantity > 0 &&
    customItem.value.unit_price >= 0
  );
});

// Auto-save watchers
let saveTimeout = null;
const debouncedSave = () => {
  if (saveTimeout) {
    clearTimeout(saveTimeout);
  }
  saveTimeout = setTimeout(() => {
    saveSession();
  }, 1000); // Save 1 second after last change
};

// Watch for changes to trigger auto-save
watch([items, notes, quoteDate, validUntil, totalDiscountType, totalDiscountValue, sendEmailOnApproval], debouncedSave, { deep: true });

// Methods
const formatDate = (date) => {
  if (!date) return '';
  return format(new Date(date), 'dd/MM/yyyy');
};

const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-PH', {
    style: 'currency',
    currency: 'PHP'
  }).format(value || 0);
};

const calculateItemTotalDiscount = (item) => {
  if (!item.discount_type || item.discount_type === 'none' || !item.discount_value || item.discount_value <= 0) {
    return 0;
  }

  const quantity = parseFloat(item.quantity || 1);
  const unitPrice = parseFloat(item.unit_price || 0);
  let totalDiscount = 0;

  if (item.discount_type === 'percentage') {
    // Percentage discount applied to unit price
    const unitDiscountAmount = (unitPrice * parseFloat(item.discount_value)) / 100;
    totalDiscount = unitDiscountAmount * quantity;
    if (item.is_dimensions && item.width && item.height) {
      // For dimensional items, multiply by area
      totalDiscount = totalDiscount * (item.width * item.height);
    }
  } else if (item.discount_type === 'fixed') {
    // Fixed discount per unit
    totalDiscount = parseFloat(item.discount_value) * quantity;
    if (item.is_dimensions && item.width && item.height) {
      // For dimensional items, multiply by area
      totalDiscount = totalDiscount * (item.width * item.height);
    }
    // Ensure discount doesn't exceed the subtotal
    const preDiscountSubtotal = parseFloat(item.pre_discount_subtotal || 0);
    totalDiscount = Math.min(totalDiscount, preDiscountSubtotal);
  }

  return totalDiscount;
};

const generateQuotationNumber = async () => {
  try {
    const response = await quotationsApi.generateNumber();
    return response.data.data.quotation_number;
  } catch (error) {
    console.error('Error generating quotation number:', error);
    return `Q-${Date.now()}`;
  }
};

const fetchProducts = async () => {
  loadingProducts.value = true;
  try {
    // Load all active products without limit for product selection
    const result = await masterData.loadProducts({ is_active: true });
    products.value = result || [];
    console.log(`Products loaded from cache: ${products.value.length} total products`);

  } catch (error) {
    console.error('Error fetching products:', error);
    toast.error('Failed to load products');
  } finally {
    loadingProducts.value = false;
  }
};

const fetchProductCategories = async () => {
  try {
    const response = await productCategoriesApi.getAll();
    productCategories.value = response.data || [];
    console.log('Product categories loaded:', productCategories.value);
  } catch (error) {
    console.error('Error fetching product categories:', error);
    toast.error('Failed to load product categories');
  }
};

const addProduct = (product) => {
  console.log('Adding product:', product);
  const newItem = {
    product_id: product.id,
    description: product.name,
    quantity: 1,
    unit_price: product.base_price ? parseFloat(product.base_price).toFixed(2) : "0.00",
    is_dimensions: product.has_dimensions,
    width: null,
    height: null,
    dimension_unit: product.dimension_unit || 'mm',
    area: null,
    is_custom_item: false,
    discount_value: 0,
    discount_type: 'none',
    pre_discount_subtotal: product.base_price ? parseFloat(product.base_price).toFixed(2) : "0.00",
    subtotal: product.base_price ? parseFloat(product.base_price).toFixed(2) : "0.00",
    unit_of_measure: product.unit_of_measure || 'pc'
  };

  items.value.push(newItem);
  showProductSelector.value = false;
};

const addCustomItem = () => {
  showCustomItemForm.value = true;
  customItem.value = {
    description: '',
    quantity: 1,
    unit_price: 0,
    is_dimensions: false,
    width: null,
    height: null,
    dimension_unit: 'mm',
    unit_of_measure: 'pc',
    discount_value: 0,
    discount_type: 'none',
    is_custom_item: true
  };
};

const saveCustomItem = () => {
  if (!isCustomItemValid.value) return;

  const newItem = { ...customItem.value };
  console.log('Saving custom item:', newItem);

  // Ensure unit price has proper decimal formatting
  newItem.unit_price = parseFloat(newItem.unit_price).toFixed(2);

  let baseSubtotal = newItem.quantity * parseFloat(newItem.unit_price);

  // If dimensions are involved, calculate based on area
  if (newItem.is_dimensions && newItem.width && newItem.height) {
    const width = parseFloat(newItem.width);
    const height = parseFloat(newItem.height);

    if (!isNaN(width) && !isNaN(height)) {
      newItem.area = width * height;
      console.log(`Dimensions: ${width} x ${height} = ${newItem.area} ${newItem.dimension_unit}²`);
      baseSubtotal = newItem.quantity * parseFloat(newItem.unit_price) * newItem.area;
    } else {
      console.warn('Invalid dimensions provided for custom item');
      newItem.area = null;
    }
  }

  // Store pre-discount subtotal
  newItem.pre_discount_subtotal = parseFloat(baseSubtotal).toFixed(2);

  // Calculate subtotal with discount
  if (newItem.discount_value && newItem.discount_value > 0 && newItem.discount_type !== 'none') {
    if (newItem.discount_type === 'percentage') {
      // Percentage discount applied to unit price
      const unitDiscountAmount = (parseFloat(newItem.unit_price) * newItem.discount_value) / 100;
      let totalDiscountAmount = unitDiscountAmount * newItem.quantity;
      if (newItem.is_dimensions && newItem.width && newItem.height) {
        // For dimensional items, multiply by area
        totalDiscountAmount = totalDiscountAmount * newItem.area;
      }
      newItem.subtotal = parseFloat(baseSubtotal - totalDiscountAmount).toFixed(2);
    } else {
      // Fixed discount per unit
      let totalDiscountAmount = newItem.discount_value * newItem.quantity;
      if (newItem.is_dimensions && newItem.width && newItem.height) {
        // For dimensional items, multiply by area
        totalDiscountAmount = totalDiscountAmount * newItem.area;
      }
      // Ensure discount doesn't exceed the subtotal
      totalDiscountAmount = Math.min(totalDiscountAmount, baseSubtotal);
      newItem.subtotal = parseFloat(baseSubtotal - totalDiscountAmount).toFixed(2);
    }
  } else {
    newItem.subtotal = parseFloat(baseSubtotal).toFixed(2);
  }

  console.log(`Custom item subtotal: ${newItem.subtotal} (pre-discount: ${newItem.pre_discount_subtotal})`);

  items.value.push(newItem);
  showCustomItemForm.value = false;
};

const cancelCustomItem = () => {
  showCustomItemForm.value = false;
};

const removeItem = (index) => {
  items.value.splice(index, 1);
};

const updateSubtotal = (index) => {
  const item = items.value[index];

  // Reset discount value if type is none
  if (item.discount_type === 'none') {
    item.discount_value = 0;
  }

  // Ensure unit price has proper decimal formatting
  item.unit_price = parseFloat(item.unit_price).toFixed(2);

  let baseSubtotal = item.quantity * parseFloat(item.unit_price);

  // If dimensions are involved, calculate based on area
  if (item.is_dimensions && item.width && item.height) {
    item.area = item.width * item.height;
    baseSubtotal = item.quantity * parseFloat(item.unit_price) * item.area;
  }

  // Store pre-discount subtotal with proper formatting
  item.pre_discount_subtotal = parseFloat(baseSubtotal).toFixed(2);

  // Apply item discount
  if (item.discount_value && item.discount_value > 0 && item.discount_type !== 'none') {
    if (item.discount_type === 'percentage') {
      // Percentage discount applied to unit price
      const unitDiscountAmount = (parseFloat(item.unit_price) * item.discount_value) / 100;
      let totalDiscountAmount = unitDiscountAmount * item.quantity;
      if (item.is_dimensions && item.width && item.height) {
        // For dimensional items, multiply by area
        totalDiscountAmount = totalDiscountAmount * item.area;
      }
      item.subtotal = parseFloat(baseSubtotal - totalDiscountAmount).toFixed(2);
    } else if (item.discount_type === 'fixed') {
      // Fixed discount per unit
      let totalDiscountAmount = item.discount_value * item.quantity;
      if (item.is_dimensions && item.width && item.height) {
        // For dimensional items, multiply by area
        totalDiscountAmount = totalDiscountAmount * item.area;
      }
      // Ensure discount doesn't exceed the subtotal
      totalDiscountAmount = Math.min(totalDiscountAmount, baseSubtotal);
      item.subtotal = parseFloat(baseSubtotal - totalDiscountAmount).toFixed(2);
    }
  } else {
    item.subtotal = parseFloat(baseSubtotal).toFixed(2);
  }
};

const updateDimensions = (index) => {
  const item = items.value[index];
  console.log(`Updating dimensions for item ${index}:`, {
    is_dimensions: item.is_dimensions,
    width: item.width,
    height: item.height,
    dimension_unit: item.dimension_unit
  });

  if (item.is_dimensions && item.width && item.height) {
    // Ensure width and height are numbers
    const width = parseFloat(item.width);
    const height = parseFloat(item.height);

    if (!isNaN(width) && !isNaN(height)) {
      console.log(`Valid dimensions: ${width} x ${height} ${item.dimension_unit}`);
      item.area = width * height;
      const baseSubtotal = item.quantity * parseFloat(item.unit_price) * item.area;

      // Store pre-discount subtotal with proper formatting
      item.pre_discount_subtotal = parseFloat(baseSubtotal).toFixed(2);

      // Apply item discount
      if (item.discount_value && item.discount_value > 0 && item.discount_type !== 'none') {
        if (item.discount_type === 'percentage') {
          // Percentage discount applied to unit price
          const unitDiscountAmount = (parseFloat(item.unit_price) * item.discount_value) / 100;
          const totalDiscountAmount = unitDiscountAmount * item.quantity * item.area;
          item.subtotal = parseFloat(baseSubtotal - totalDiscountAmount).toFixed(2);
        } else if (item.discount_type === 'fixed') {
          // Fixed discount per unit
          let totalDiscountAmount = item.discount_value * item.quantity * item.area;
          // Ensure discount doesn't exceed the subtotal
          totalDiscountAmount = Math.min(totalDiscountAmount, baseSubtotal);
          item.subtotal = parseFloat(baseSubtotal - totalDiscountAmount).toFixed(2);
        }
      } else {
        item.subtotal = parseFloat(baseSubtotal).toFixed(2);
      }
      console.log(`Calculated subtotal: ${item.subtotal} (pre-discount: ${item.pre_discount_subtotal})`);
    } else {
      console.warn(`Invalid dimensions: width=${item.width}, height=${item.height}`);
      // If dimensions are invalid, fall back to regular calculation
      updateSubtotal(index);
    }
  } else {
    // If dimensions are not applicable, fall back to regular calculation
    console.log('Dimensions not applicable, using regular calculation');
    updateSubtotal(index);
  }
};

const updateTotalDiscount = () => {
  // Reset discount value if type is none
  if (totalDiscountType.value === 'none') {
    totalDiscountValue.value = 0;
  }

  let amount = 0;
  if (totalDiscountType.value !== 'none' && totalDiscountValue.value > 0) {
    if (totalDiscountType.value === 'percentage') {
      amount = (parseFloat(subtotalAmount.value) * totalDiscountValue.value) / 100;
      console.log(`Calculating percentage discount: ${totalDiscountValue.value}% of ${subtotalAmount.value} = ${amount}`);
    } else if (totalDiscountType.value === 'fixed') {
      amount = Math.min(totalDiscountValue.value, parseFloat(subtotalAmount.value));
      console.log(`Calculating fixed discount: min(${totalDiscountValue.value}, ${subtotalAmount.value}) = ${amount}`);
    }
  }

  // Ensure we're working with a valid number
  amount = isNaN(amount) ? 0 : amount;

  totalDiscountAmount.value = parseFloat(amount).toFixed(2);
  console.log(`Total discount amount set to: ${totalDiscountAmount.value}`);
};

const saveAsDraft = async () => {
  await saveQuotation('draft');
};

const generateQuotation = async () => {
  await saveQuotation('pending');
};

const saveQuotation = async (status) => {
  if (items.value.length === 0) {
    toast.warning('Please add at least one item to the quotation');
    return;
  }

  loading.value = true;

  try {
    // Debug logs for client data
    console.log('Client prop:', props.client);
    console.log('Client ID:', props.client?.id);
    console.log('Local client:', localClient.value);

    // Get client ID based on mode
    const clientId = props.editMode ? localClient.value?.id : props.client?.id;

    // Validate client ID
    if (!clientId) {
      toast.error('Client ID is missing. Please ensure a client is selected.');
      loading.value = false;
      return;
    }

    // Format dates to avoid timezone issues
    const formatDateForAPI = (date) => {
      if (!date) return null;
      const d = new Date(date);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    // Prepare quotation data
    const quotationData = {
      quotation: {
        client_id: clientId,
        quote_date: formatDateForAPI(quoteDate.value),
        valid_until: formatDateForAPI(validUntil.value),
        subtotal_amount: parseFloat(subtotalAmount.value),
        total_discount_type: totalDiscountType.value,
        total_discount_value: parseFloat(totalDiscountValue.value),
        total_discount: parseFloat(totalDiscountAmount.value),
        total_amount: parseFloat(totalAmount.value),
        status: status,
        notes: notes.value,
        send_email_on_approval: sendEmailOnApproval.value
      },
      items: items.value.map(item => ({
        ...item,
        unit_price: parseFloat(item.unit_price),
        pre_discount_subtotal: parseFloat(item.pre_discount_subtotal),
        subtotal: parseFloat(item.subtotal),
        discount_value: parseFloat(item.discount_value || 0),
        // Clear discount_amount to force backend recalculation
        discount_amount: null
      }))
    };

    // Debug log
    console.log('Sending quotation data:', JSON.stringify(quotationData, null, 2));

    let response;

    // Update or create based on edit mode
    if (props.editMode && props.quotationId) {
      console.log(`Updating quotation ID: ${props.quotationId}`);
      response = await quotationsApi.update(props.quotationId, quotationData);
      console.log('Update response:', response);
      toast.success(`Quotation updated successfully`);
    } else {
      response = await quotationsApi.create(quotationData);
      toast.success(`Quotation ${status === 'draft' ? 'saved as draft' : 'generated'} successfully`);
    }

    // Clear saved session on successful creation/update
    clearSession();

    // Close modal
    show.value = false;

    // Emit created event
    emit('created');
  } catch (error) {
    console.error('Error saving quotation:', error);
    toast.error(`Failed to save quotation: ${error.response?.data?.message || error.message}`);
  } finally {
    loading.value = false;
  }
};

// Load existing quotation for editing
const loadQuotation = async () => {
  if (!props.quotationId) return;

  loading.value = true;

  try {
    const response = await quotationsApi.getById(props.quotationId);
    const quotation = response.data.data;

    console.log('Loaded quotation data:', quotation); // Debug log

    // Set quotation details
    quotationNumber.value = quotation.quotation_number;
    quoteDate.value = quotation.quote_date;
    validUntil.value = quotation.valid_until;
    notes.value = quotation.notes || '';
    sendEmailOnApproval.value = quotation.send_email_on_approval !== undefined ? quotation.send_email_on_approval : true;

    // Set discount values
    totalDiscountType.value = quotation.total_discount_type || 'none';
    totalDiscountValue.value = quotation.total_discount_value || 0;
    totalDiscountAmount.value = quotation.total_discount || 0;

    // Set items
    items.value = quotation.items.map(item => {
      // Ensure dimensions are properly handled
      const is_dimensions = Boolean(item.is_dimensions || (item.width && item.height));
      const width = item.width ? parseFloat(item.width) : null;
      const height = item.height ? parseFloat(item.height) : null;
      const dimension_unit = item.dimension_unit || 'mm';

      console.log(`Processing item: ${item.description}`, {
        is_dimensions,
        width,
        height,
        dimension_unit
      });

      // Calculate correct pre-discount subtotal
      let correctPreDiscountSubtotal;
      if (item.pre_discount_subtotal && parseFloat(item.pre_discount_subtotal) > 0) {
        // Use existing value if it's valid
        correctPreDiscountSubtotal = parseFloat(item.pre_discount_subtotal);
      } else {
        // Recalculate from scratch
        correctPreDiscountSubtotal = parseFloat(item.quantity) * parseFloat(item.unit_price);
        if (is_dimensions && width && height) {
          const area = width * height;
          correctPreDiscountSubtotal = parseFloat(item.quantity) * parseFloat(item.unit_price) * area;
          console.log(`Recalculated with dimensions: ${item.quantity} * ${item.unit_price} * ${area} = ${correctPreDiscountSubtotal}`);
        }
      }

      return {
        ...item,
        pre_discount_subtotal: correctPreDiscountSubtotal,
        subtotal: parseFloat(item.subtotal),
        unit_price: parseFloat(item.unit_price),
        quantity: parseFloat(item.quantity),
        discount_value: parseFloat(item.discount_value || 0),
        discount_type: item.discount_type || 'none',
        is_custom_item: item.is_custom_item || !item.product_id,
        // Set dimensions properties
        is_dimensions,
        width,
        height,
        dimension_unit,
        area: is_dimensions && width && height ? width * height : null
      };
    });

    // Recalculate subtotals for all items to ensure proper values
    console.log('Recalculating subtotals for all items...');
    items.value.forEach((item, index) => {
      if (item.is_dimensions && item.width && item.height) {
        updateDimensions(index);
      } else {
        updateSubtotal(index);
      }
    });

    // Update client data
    if (quotation.client_id) {
      localClient.value = {
        id: quotation.client_id,
        first_name: quotation.first_name,
        last_name: quotation.last_name,
        company_name: quotation.company_name,
        email: quotation.email,
        phone: quotation.phone,
        address: quotation.address
      };
      console.log('Set local client data:', localClient.value);
    } else {
      console.error('No client_id found in quotation data');
      toast.error('Client information is missing from the quotation');
    }

  } catch (error) {
    console.error('Error loading quotation:', error);
    toast.error(`Failed to load quotation: ${error.response?.data?.message || error.message}`);
  } finally {
    loading.value = false;
  }
};

// Session restoration methods
const restoreSession = async () => {
  try {
    const restored = loadSession();
    if (restored) {
      showRestoreConfirmation.value = false;
      console.log('✅ Session restored successfully');
    } else {
      toast.error('Failed to restore session');
      showRestoreConfirmation.value = false;
      // Generate quotation number if restore failed
      quotationNumber.value = await generateQuotationNumber();
    }
  } catch (error) {
    console.error('Error restoring session:', error);
    toast.error('Failed to restore session');
    showRestoreConfirmation.value = false;
    quotationNumber.value = await generateQuotationNumber();
  }
};

const discardSession = async () => {
  clearSession();
  showRestoreConfirmation.value = false;
  hasSavedSession.value = false;

  // Generate fresh quotation number
  quotationNumber.value = await generateQuotationNumber();

  toast.info('Starting fresh quotation');
};

// Lifecycle hooks
onMounted(async () => {
  try {
    if (props.editMode && props.quotationId) {
      // Load existing quotation
      await loadQuotation();
    } else {
      // Check for saved session and ask user if they want to restore
      await nextTick(); // Wait for props to be fully set

      const savedSessionExists = (() => {
        try {
          const savedData = localStorage.getItem(sessionKey.value);
          if (!savedData) return false;

          const sessionData = JSON.parse(savedData);
          const sessionTime = new Date(sessionData.timestamp);
          const now = new Date();
          const hoursDiff = (now - sessionTime) / (1000 * 60 * 60);

          return hoursDiff <= 24 &&
            sessionData.items?.length > 0 &&
            sessionData.client?.id === props.client?.id;
        } catch {
          return false;
        }
      })();

      if (savedSessionExists) {
        hasSavedSession.value = true;
        showRestoreConfirmation.value = true;
      } else {
        // Generate quotation number for new quotation
        quotationNumber.value = await generateQuotationNumber();
      }
    }

    // Fetch products and categories
    await Promise.all([
      fetchProducts(),
      fetchProductCategories()
    ]);
  } catch (error) {
    console.error('Error initializing quotation form:', error);
  }
});

// Cleanup on unmount
onBeforeUnmount(() => {
  if (saveTimeout) {
    clearTimeout(saveTimeout);
  }
});

// Watch for changes in quotationId
watch(() => props.quotationId, async (newVal) => {
  if (newVal && props.editMode) {
    await loadQuotation();
  }
});
</script>